export interface PasskeyStatus {
  enabled: boolean;
  authenticated: boolean;
  hasPasskeys: boolean;
  csrfToken?: string;
}

let csrfToken: string | undefined;

export async function passkeyRequest<T>(
  path: string,
  body?: unknown,
  method = body === undefined ? 'GET' : 'POST'
): Promise<T> {
  const base =
    (window as unknown as { showrunnerLogin?: { urlBase: string } })
      .showrunnerLogin?.urlBase ?? window.Showrunner.urlBase;
  const response = await fetch(`${base}/auth/passkeys/${path}`, {
    method,
    credentials: 'same-origin',
    cache: 'no-store',
    headers: {
      'Content-Type': 'application/json',
      'X-Showrunner-Request': 'passkey',
      ...(csrfToken ? { 'X-Showrunner-CSRF': csrfToken } : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(
      error.message ??
        (response.status === 429
          ? 'Too many attempts. Please try again in a minute.'
          : 'Sign in with your passkey again to continue.')
    );
  }
  return response.status === 204 ? (undefined as T) : response.json();
}

export async function passkeyStatus() {
  const status = await passkeyRequest<PasskeyStatus>('status');
  csrfToken = status.csrfToken;
  return status;
}

function decode(value: string): ArrayBuffer {
  const bytes = Uint8Array.from(
    atob(value.replace(/-/g, '+').replace(/_/g, '/')),
    (character) => character.charCodeAt(0)
  );
  return bytes.buffer;
}

function encode(value: ArrayBuffer) {
  return btoa(String.fromCharCode(...new Uint8Array(value)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

export async function usePasskey(enrollment?: {
  token?: string;
  name: string;
}) {
  if (!window.isSecureContext || !window.PublicKeyCredential)
    throw new Error('Open Showrunner at its HTTPS address to use a passkey.');
  if (enrollment) {
    const options = await passkeyRequest<
      Omit<
        PublicKeyCredentialCreationOptions,
        'challenge' | 'user' | 'excludeCredentials'
      > & {
        challenge: string;
        user: Omit<PublicKeyCredentialUserEntity, 'id'> & { id: string };
        excludeCredentials: (Omit<PublicKeyCredentialDescriptor, 'id'> & {
          id: string;
        })[];
      }
    >('register/options', {
      enrollmentToken: enrollment.token,
      name: enrollment.name,
    });
    const credential = (await navigator.credentials.create({
      publicKey: {
        ...options,
        challenge: decode(options.challenge),
        user: { ...options.user, id: decode(options.user.id) },
        excludeCredentials: options.excludeCredentials?.map((item) => ({
          ...item,
          id: decode(item.id),
        })),
      },
    })) as PublicKeyCredential | null;
    if (!credential) throw new Error('No passkey was created.');
    const response = credential.response as AuthenticatorAttestationResponse;
    await passkeyRequest('register/verify', {
      id: credential.id,
      rawId: encode(credential.rawId),
      type: credential.type,
      response: {
        clientDataJSON: encode(response.clientDataJSON),
        attestationObject: encode(response.attestationObject),
        transports: response.getTransports?.() ?? [],
      },
      clientExtensionResults: credential.getClientExtensionResults(),
    });
  } else {
    const options = await passkeyRequest<
      Omit<
        PublicKeyCredentialRequestOptions,
        'challenge' | 'allowCredentials'
      > & {
        challenge: string;
        allowCredentials?: (Omit<PublicKeyCredentialDescriptor, 'id'> & {
          id: string;
        })[];
      }
    >('login/options', {});
    const credential = (await navigator.credentials.get({
      publicKey: {
        ...options,
        challenge: decode(options.challenge),
        allowCredentials: options.allowCredentials?.map((item) => ({
          ...item,
          id: decode(item.id),
        })),
      },
    })) as PublicKeyCredential | null;
    if (!credential) throw new Error('No passkey was selected.');
    const response = credential.response as AuthenticatorAssertionResponse;
    await passkeyRequest('login/verify', {
      id: credential.id,
      rawId: encode(credential.rawId),
      type: credential.type,
      response: {
        clientDataJSON: encode(response.clientDataJSON),
        authenticatorData: encode(response.authenticatorData),
        signature: encode(response.signature),
        userHandle: response.userHandle ? encode(response.userHandle) : null,
      },
      clientExtensionResults: credential.getClientExtensionResults(),
    });
  }
  return passkeyStatus();
}
